import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useQuery, useQueryClient, UseQueryOptions } from 'react-query'
import { BrandMediaFile, BrandMediaType, BrandVisualKit } from 'types'
import { sendToApi } from 'api/client'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import qs from 'qs'

interface Data {
  brand_media_files: BrandMediaFile[]
}

interface Props {
  brandVisualKitId: number
  brandMediaType?: BrandMediaType[]
  isPrimaryLogo?: boolean
}

type CacheData = { brand_visual_kits: BrandVisualKit[] } | undefined

const fetchBrandMedialFiles = (
  brandVisualKitId: number,
  workspaceId: number,
  brandMediaType?: BrandMediaType[],
  isPrimaryLogo?: boolean,
) => {
  const params = qs.stringify(
    { brand_media_type: brandMediaType, is_primary_logo: isPrimaryLogo },
    { arrayFormat: 'brackets' },
  )
  return sendToApi<Data>(`/w/${workspaceId}/brand_visual_kits/${brandVisualKitId}/brand_media_files?${params}`)
}

export const useBrandMediaFiles = (
  { brandVisualKitId, brandMediaType, isPrimaryLogo }: Props,
  options?: UseQueryOptions<Data>,
) => {
  const currentWorkspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()

  return useQuery<Data>(
    [
      'workspaces',
      currentWorkspaceId,
      'brand_visual_kits',
      brandVisualKitId,
      'brand_media_files',
      'list',
      { brandMediaType, isPrimaryLogo },
    ],
    async () => {
      const res = await fetchBrandMedialFiles(brandVisualKitId, currentWorkspaceId, brandMediaType, isPrimaryLogo)
      if (res.type !== SUCCESS_RESPONSE_TYPE) {
        throw new Error()
      }
      return res.data
    },
    {
      initialData: () => {
        const existingBrandVisualKit = queryClient
          .getQueryData<CacheData>(['workspaces', currentWorkspaceId, 'brand_visual_kits', 'list'])
          ?.brand_visual_kits.find((brandVisualKit) => brandVisualKit.id === brandVisualKitId)

        if (!existingBrandVisualKit?.primary_logo || !brandMediaType?.includes(BrandMediaType.LOGO)) return

        return {
          brand_media_files: [existingBrandVisualKit.primary_logo],
        }
      },
      refetchOnWindowFocus: false,
      ...options,
    },
  )
}
